import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Category } from '../types';
import { categoryService } from '../services/categoryService';
import { toast } from 'react-toastify';

const CategoriesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  text-align: center;
  color: var(--primary-color);
  margin-bottom: 2rem;
  font-size: 2.5rem;
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const CategoryCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border-color: var(--primary-color);
  }
`;

const CategoryName = styled.h3`
  color: var(--primary-color);
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const CategoryDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: #7f8c8d;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #7f8c8d;
  font-size: 1.1rem;
`;

const Categories: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const data = await categoryService.getCategories();
        setCategories(data);
      } catch (error) {
        console.error('Error loading categories:', error);
        toast.error('Error al cargar las categorías');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleCategoryClick = (categoryId: string, categoryName: string) => {
    navigate(`/productos?categoria=${categoryName}`);
  };

  if (loading) {
    return (
      <CategoriesContainer>
        <LoadingContainer>
          Cargando categorías...
        </LoadingContainer>
      </CategoriesContainer>
    );
  }

  return (
    <CategoriesContainer>
      <Title>Nuestras Categorías</Title>
      
      {categories.length === 0 ? (
        <EmptyState>
          No hay categorías disponibles en este momento.
        </EmptyState>
      ) : (
        <CategoriesGrid>
          {categories.map((category) => (
            <CategoryCard
              key={category.id}
              onClick={() => handleCategoryClick(category.id, category.name)}
            >
              <CategoryName>{category.name}</CategoryName>
              {category.description && (
                <CategoryDescription>{category.description}</CategoryDescription>
              )}
            </CategoryCard>
          ))}
        </CategoriesGrid>
      )}
    </CategoriesContainer>
  );
};

export default Categories;